/**
 * 个人中心页面
 * 显示历史练习记录和个人信息
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  SafeAreaView,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { logout } from '../store/authSlice';
import AuthService from '../services/auth';
import { APP_CONFIG } from '../config/version';

interface PracticeRecord {
  id: string;
  date: string;
  exercises: Array<{
    chineseText: string;
    userAnswer: string;
    score: number;
    feedback: string;
  }>;
  totalScore: number;
  completedCount: number;
}

interface ProfileScreenProps {
  onBackToChat: () => void;
  onNavigateToPractice: (exercises: any[]) => void;
  onInterfaceStyleChange?: (style: 'game' | 'traditional') => void;
  onNavigateToWallet?: () => void;
}

const ProfileScreen: React.FC<ProfileScreenProps> = ({ onBackToChat, onNavigateToPractice, onInterfaceStyleChange, onNavigateToWallet }) => {
  const dispatch = useAppDispatch();
  const { user, isGuestMode, guestUsage } = useAppSelector((state) => state.auth);
  const [practiceHistory, setPracticeHistory] = useState<PracticeRecord[]>([]);
  const [currentInterfaceStyle, setCurrentInterfaceStyle] = useState<'game' | 'traditional'>(APP_CONFIG.CHAT_INTERFACE_STYLE);

  useEffect(() => {
    loadPracticeHistory();
    loadInterfaceStylePreference();
  }, []);

  const loadInterfaceStylePreference = async () => {
    try {
      const savedStyle = await AsyncStorage.getItem('chatInterfaceStyle');
      if (savedStyle && (savedStyle === 'game' || savedStyle === 'traditional')) {
        setCurrentInterfaceStyle(savedStyle);
      }
    } catch (error) {
      console.log('加载界面风格偏好失败:', error);
    }
  };

  const saveInterfaceStylePreference = async (style: 'game' | 'traditional') => {
    try {
      await AsyncStorage.setItem('chatInterfaceStyle', style);
      setCurrentInterfaceStyle(style);
      onInterfaceStyleChange?.(style);

      Alert.alert(
        '设置已保存',
        `界面风格已切换到${style === 'game' ? '游戏风格' : '传统聊天'}界面`,
        [{ text: '确定' }]
      );
    } catch (error) {
      console.log('保存界面风格偏好失败:', error);
      Alert.alert('错误', '保存设置失败，请重试');
    }
  };

  const loadPracticeHistory = () => {
    // 从本地存储加载练习历史记录
    // 这里先用模拟数据，实际应该从AsyncStorage或后端加载
    const mockHistory: PracticeRecord[] = [
      {
        id: '1',
        date: '2024-01-15',
        exercises: [
          {
            chineseText: '我今天过得很愉快',
            userAnswer: 'I had a great day today',
            score: 90,
            feedback: '很棒！翻译准确且自然',
          },
          {
            chineseText: '这本书很有趣',
            userAnswer: 'This book is interesting',
            score: 85,
            feedback: '不错，可以尝试用更生动的形容词',
          },
          {
            chineseText: '我喜欢学习英语',
            userAnswer: 'I like learning English',
            score: 95,
            feedback: '完美的翻译！',
          },
        ],
        totalScore: 270,
        completedCount: 3,
      },
      {
        id: '2',
        date: '2024-01-14',
        exercises: [
          {
            chineseText: '今天天气很好',
            userAnswer: 'The weather is good today',
            score: 80,
            feedback: '不错，也可以说 "nice" 或 "beautiful"',
          },
          {
            chineseText: '我想去公园散步',
            userAnswer: 'I want to go to the park for a walk',
            score: 92,
            feedback: '很好的表达方式！',
          },
        ],
        totalScore: 172,
        completedCount: 2,
      },
    ];

    setPracticeHistory(mockHistory);
  };

  const handleLogout = async () => {
    // Web兼容的退出确认
    const performLogout = async () => {
      try {
        await AuthService.logout();
        dispatch(logout());
      } catch (error) {
        console.error('Logout error:', error);
        dispatch(logout());
      }
    };

    if (Platform.OS === 'web') {
      const confirmed = window.confirm('确认退出\n\n您确定要退出登录吗？');
      if (confirmed) {
        await performLogout();
      }
    } else {
      Alert.alert(
        '确认退出',
        '您确定要退出登录吗？',
        [
          { text: '取消', style: 'cancel' },
          {
            text: '退出',
            style: 'destructive',
            onPress: performLogout,
          },
        ]
      );
    }
  };

  const repeatPractice = (record: PracticeRecord) => {
    // 重新练习，将历史题目转换为练习格式
    const exercises = record.exercises.map((ex, index) => ({
      id: `repeat_${record.id}_${index}`,
      chineseText: ex.chineseText,
      difficulty: 'medium' as const,
      category: '重复练习',
      context: '来自历史记录的重复练习',
    }));

    onNavigateToPractice(exercises);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getAverageScore = (record: PracticeRecord) => {
    return record.exercises.length > 0
      ? Math.round(record.totalScore / record.exercises.length)
      : 0;
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* 头部导航 */}
      <View style={styles.headerContainer}>
        <TouchableOpacity style={styles.backButton} onPress={onBackToChat}>
          <Text style={styles.backButtonText}>← 返回</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>个人中心</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView style={styles.scrollContainer}>
        {/* 用户信息卡片 */}
        <View style={styles.userCard}>
          <View style={styles.avatarContainer}>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>
                {isGuestMode ? '游' : (user?.display_name?.charAt(0) || user?.username?.charAt(0) || 'U')}
              </Text>
            </View>
          </View>

          <View style={styles.userInfo}>
            <Text style={styles.username}>
              {isGuestMode ? '游客用户' : user?.display_name || user?.username || '未知用户'}
            </Text>
            <Text style={styles.userStatus}>
              {isGuestMode ? '体验模式' : '正式用户'}
            </Text>
          </View>

          {!isGuestMode && (
            <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
              <Text style={styles.logoutButtonText}>退出</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* 使用统计 */}
        <View style={styles.statsCard}>
          <Text style={styles.sectionTitle}>使用统计</Text>
          <View style={styles.statsGrid}>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>{practiceHistory.length}</Text>
              <Text style={styles.statLabel}>练习天数</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>
                {practiceHistory.reduce((sum, record) => sum + record.completedCount, 0)}
              </Text>
              <Text style={styles.statLabel}>完成题目</Text>
            </View>
            <View style={styles.statItem}>
              <Text style={styles.statNumber}>
                {practiceHistory.length > 0
                  ? Math.round(practiceHistory.reduce((sum, record) => sum + getAverageScore(record), 0) / practiceHistory.length)
                  : 0}
              </Text>
              <Text style={styles.statLabel}>平均分数</Text>
            </View>
          </View>

          {isGuestMode && (
            <View style={styles.guestUsageContainer}>
              <Text style={styles.guestUsageText}>
                游客模式 - 已使用 {guestUsage.practiceCount}/5 次练习
              </Text>
            </View>
          )}
        </View>

        {/* 钱包功能 */}
        {onNavigateToWallet && (
          <View style={styles.settingsCard}>
            <Text style={styles.sectionTitle}>钱包与付费功能</Text>
            
            <TouchableOpacity style={styles.walletButton} onPress={onNavigateToWallet}>
              <View style={styles.walletButtonContent}>
                <Text style={styles.walletButtonIcon}>💰</Text>
                <View style={styles.walletButtonInfo}>
                  <Text style={styles.walletButtonTitle}>我的钱包</Text>
                  <Text style={styles.walletButtonDescription}>
                    查看余额、充值记录和消费明细
                  </Text>
                </View>
                <Text style={styles.walletButtonArrow}>→</Text>
              </View>
            </TouchableOpacity>
          </View>
        )}

        {/* 界面设置 */}
        <View style={styles.settingsCard}>
          <Text style={styles.sectionTitle}>界面设置</Text>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>聊天界面风格</Text>
              <Text style={styles.settingDescription}>
                选择您喜欢的聊天界面风格
              </Text>
            </View>
          </View>

          <View style={styles.interfaceStyleOptions}>
            <TouchableOpacity
              style={[
                styles.styleOptionButton,
                currentInterfaceStyle === 'game' && styles.styleOptionSelected,
              ]}
              onPress={() => saveInterfaceStylePreference('game')}
            >
              <View style={styles.styleOptionContent}>
                <Text style={styles.styleOptionEmoji}>🎮</Text>
                <Text style={[
                  styles.styleOptionTitle,
                  currentInterfaceStyle === 'game' && styles.styleOptionTitleSelected,
                ]}>
                  游戏风格
                </Text>
                <Text style={styles.styleOptionDescription}>
                  视觉小说风格，公主对话界面
                </Text>
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.styleOptionButton,
                currentInterfaceStyle === 'traditional' && styles.styleOptionSelected,
              ]}
              onPress={() => saveInterfaceStylePreference('traditional')}
            >
              <View style={styles.styleOptionContent}>
                <Text style={styles.styleOptionEmoji}>💬</Text>
                <Text style={[
                  styles.styleOptionTitle,
                  currentInterfaceStyle === 'traditional' && styles.styleOptionTitleSelected,
                ]}>
                  传统聊天
                </Text>
                <Text style={styles.styleOptionDescription}>
                  经典聊天界面，简洁高效
                </Text>
              </View>
            </TouchableOpacity>
          </View>
        </View>

        {/* 练习历史 */}
        <View style={styles.historySection}>
          <Text style={styles.sectionTitle}>练习历史</Text>

          {practiceHistory.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>还没有练习记录</Text>
              <Text style={styles.emptyStateSubtext}>开始你的第一次翻译练习吧！</Text>
            </View>
          ) : (
            practiceHistory.map((record) => (
              <View key={record.id} style={styles.historyCard}>
                <View style={styles.historyHeader}>
                  <Text style={styles.historyDate}>{formatDate(record.date)}</Text>
                  <View style={styles.historyScore}>
                    <Text style={styles.scoreText}>{getAverageScore(record)}分</Text>
                  </View>
                </View>

                <View style={styles.historyDetails}>
                  <Text style={styles.historyInfo}>
                    完成 {record.completedCount} 道题目 · 总分 {record.totalScore} 分
                  </Text>
                </View>

                {/* 练习题目预览 */}
                <View style={styles.exercisePreview}>
                  {record.exercises.slice(0, 2).map((exercise, index) => (
                    <View key={index} style={styles.exerciseItem}>
                      <Text style={styles.exerciseText} numberOfLines={1}>
                        {exercise.chineseText}
                      </Text>
                      <Text style={styles.exerciseScore}>
                        {exercise.score}分
                      </Text>
                    </View>
                  ))}
                  {record.exercises.length > 2 && (
                    <Text style={styles.moreExercises}>
                      还有 {record.exercises.length - 2} 道题目...
                    </Text>
                  )}
                </View>

                <TouchableOpacity
                  style={styles.repeatButton}
                  onPress={() => repeatPractice(record)}
                >
                  <Text style={styles.repeatButtonText}>重新练习</Text>
                </TouchableOpacity>
              </View>
            ))
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  backButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#dee2e6',
  },
  backButtonText: {
    fontSize: 14,
    color: '#6c757d',
    fontWeight: '500',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  headerSpacer: {
    width: 80,
  },
  scrollContainer: {
    flex: 1,
    padding: 16,
  },
  userCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  avatarContainer: {
    marginRight: 16,
  },
  avatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#3498db',
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
  },
  userInfo: {
    flex: 1,
  },
  username: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  userStatus: {
    fontSize: 14,
    color: '#666',
  },
  logoutButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#e74c3c',
    borderRadius: 6,
  },
  logoutButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  statsCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#3498db',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: '#666',
  },
  guestUsageContainer: {
    marginTop: 16,
    padding: 12,
    backgroundColor: '#fff3cd',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ffeaa7',
  },
  guestUsageText: {
    fontSize: 14,
    color: '#856404',
    textAlign: 'center',
  },
  historySection: {
    marginBottom: 20,
  },
  emptyState: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 40,
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#666',
    marginBottom: 8,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#999',
  },
  historyCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
  },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  historyDate: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  historyScore: {
    backgroundColor: '#e8f5e8',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  scoreText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#27ae60',
  },
  historyDetails: {
    marginBottom: 12,
  },
  historyInfo: {
    fontSize: 14,
    color: '#666',
  },
  exercisePreview: {
    marginBottom: 12,
  },
  exerciseItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  exerciseText: {
    flex: 1,
    fontSize: 14,
    color: '#333',
    marginRight: 8,
  },
  exerciseScore: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  moreExercises: {
    fontSize: 12,
    color: '#999',
    fontStyle: 'italic',
    marginTop: 4,
  },
  repeatButton: {
    backgroundColor: '#3498db',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  repeatButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
  },
  // 界面设置样式
  settingsCard: {
    backgroundColor: '#fff',
    marginHorizontal: 20,
    marginBottom: 16,
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  settingItem: {
    marginBottom: 16,
  },
  settingInfo: {
    marginBottom: 4,
  },
  settingLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  settingDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  interfaceStyleOptions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  styleOptionButton: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    padding: 16,
    borderWidth: 2,
    borderColor: '#e9ecef',
    marginHorizontal: 6,
  },
  styleOptionSelected: {
    backgroundColor: '#e8f5e8',
    borderColor: '#4CAF50',
  },
  styleOptionContent: {
    alignItems: 'center',
  },
  styleOptionEmoji: {
    fontSize: 24,
    marginBottom: 8,
  },
  styleOptionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
    textAlign: 'center',
  },
  styleOptionTitleSelected: {
    color: '#2E7D32',
  },
  styleOptionDescription: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    lineHeight: 16,
  },
  // 钱包按钮样式
  walletButton: {
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    marginTop: 8,
  },
  walletButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  walletButtonIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  walletButtonInfo: {
    flex: 1,
  },
  walletButtonTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  walletButtonDescription: {
    fontSize: 14,
    color: '#6c757d',
    lineHeight: 18,
  },
  walletButtonArrow: {
    fontSize: 18,
    color: '#007bff',
    fontWeight: '600',
  },
});

export default ProfileScreen;
